import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { Trophy, Users, Plus, Eye, ArrowLeft, Clock, XCircle, RefreshCcw, X } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import { getHomeLeagues, type HomeLeagueRow } from "@/lib/home.functions";
import {
  getMyLeagueRequests,
  cancelMyLeagueRequest,
  resubmitMyLeagueRequest,
  type MyLeagueRequest,
} from "@/lib/access.functions";
import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { formatDistanceToNowStrict } from "date-fns";
import goblinMark from "@/assets/mascot-logo-mark.png";

type HomeSearch = { as?: "member" };

export const Route = createFileRoute("/_app/home")({
  validateSearch: (search: Record<string, unknown>): HomeSearch => ({
    as: search.as === "member" ? "member" : undefined,
  }),
  component: AppHome,
});

function AppHome() {
  const { user, isAdmin, profile } = useAuth();
  const navigate = useNavigate();
  const { as } = useSearch({ from: "/_app/home" });
  const memberView = as === "member";
  const loadHomeLeagues = useAuthedServerFn(getHomeLeagues);
  const loadMyRequests = useAuthedServerFn(getMyLeagueRequests);
  const cancelRequestFn = useAuthedServerFn(cancelMyLeagueRequest);
  const resubmitRequestFn = useAuthedServerFn(resubmitMyLeagueRequest);
  const [leagues, setLeagues] = useState<HomeLeagueRow[]>([]);
  const [requests, setRequests] = useState<MyLeagueRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    const [leaguesList, reqList] = await Promise.all([
      loadHomeLeagues().catch((e) => {
        console.error("[home] failed to load leagues", e);
        return [] as HomeLeagueRow[];
      }),
      loadMyRequests().catch((e) => {
        console.error("[home] failed to load requests", e);
        return [] as MyLeagueRequest[];
      }),
    ]);
    setLeagues(leaguesList);
    setRequests(reqList);
  }, [loadHomeLeagues, loadMyRequests]);

  useEffect(() => {
    if (!user) return;
    if (isAdmin && !memberView) {
      navigate({ to: "/admin" });
      return;
    }
    let cancelled = false;
    let isFirstLoad = true;

    const tick = async () => {
      try {
        await refetch();
      } finally {
        if (!cancelled && isFirstLoad) {
          setLoading(false);
          isFirstLoad = false;
        }
      }
    };

    void tick();

    const onFocus = () => {
      if (document.visibilityState === "visible") void tick();
    };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    const interval = window.setInterval(() => void tick(), 30_000);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
      window.clearInterval(interval);
    };
  }, [user, isAdmin, memberView, navigate, refetch]);

  const handleCancel = async (req: MyLeagueRequest) => {
    if (actingId) return;
    setActingId(req.membershipId);
    try {
      await cancelRequestFn({ data: { membershipId: req.membershipId } });
      toast.success(
        req.status === "denied"
          ? "Removed from your list."
          : `Cancelled your request to join ${req.leagueName}.`,
      );
      await refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't cancel that request.");
    } finally {
      setActingId(null);
    }
  };

  const handleResubmit = async (req: MyLeagueRequest) => {
    if (actingId) return;
    setActingId(req.membershipId);
    try {
      await resubmitRequestFn({ data: { membershipId: req.membershipId } });
      toast.success(`Re-submitted your request to join ${req.leagueName}.`);
      await refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't re-submit that request.");
    } finally {
      setActingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Trophy className="h-5 w-5 animate-pulse text-primary" />
      </div>
    );
  }

  return (
    <>
      {isAdmin && memberView && (
        <div className="border-b border-amber-500/30 bg-amber-500/10">
          <div className="mx-auto flex max-w-2xl flex-wrap items-center justify-between gap-2 px-4 py-2 text-xs sm:text-sm">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <Eye className="h-4 w-4 shrink-0" />
              <span>Viewing the app as a member</span>
            </div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-background/60 px-2 py-1 font-medium text-amber-700 transition-colors hover:bg-amber-500/20 dark:text-amber-300"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Exit member view
            </Link>
          </div>
        </div>
      )}
    <main className="max-w-2xl mx-auto px-4 py-12">
      <header className="mb-8 flex items-start gap-3">
        <img
          src={goblinMark}
          alt=""
          className="h-12 w-12 shrink-0 mt-1"
        />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-2">
            Welcome back
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground">
            {profile?.first_name || "Hey there"}
          </h1>
        </div>
      </header>

      {isAdmin && (
        <div className="mb-6 rounded-xl border border-primary/30 bg-primary/5 p-4 flex items-center justify-between">
          <div>
            <div className="font-semibold text-foreground">Admin tools</div>
            <div className="text-xs text-muted-foreground">
              Review pending requests, manage leagues
            </div>
          </div>
          <Button asChild size="sm">
            <Link to="/admin">Open admin</Link>
          </Button>
        </div>
      )}

      <h2 className="font-display text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
        <Users className="w-4 h-4 text-primary" /> Your leagues
      </h2>

      {leagues.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <div className="text-sm text-muted-foreground mb-4">
            {isAdmin && memberView
              ? "You haven't joined any leagues yet. As an admin you can still open any league from the dashboard."
              : "You're not in any leagues yet."}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {isAdmin && memberView ? (
              <Button asChild>
                <Link to="/admin">
                  <ArrowLeft className="w-4 h-4" /> Back to admin dashboard
                </Link>
              </Button>
            ) : (
              <Button asChild>
                <Link to="/join-league">
                  <Plus className="w-4 h-4" /> Join a league with a code
                </Link>
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {leagues.map((lg) => {
            const noEntry = lg.entryCount === 0;
            const isMulti = lg.maxEntries > 1;
            let badgeText: string | null = null;
            let badgeWarn = false;
            if (isMulti) {
              badgeText = `${lg.entryCount} / ${lg.maxEntries} brackets`;
              badgeWarn = noEntry;
            } else if (noEntry) {
              badgeText = "No bracket yet";
              badgeWarn = true;
            }
            return (
              <Link
                key={lg.id}
                to="/$league"
                params={{ league: lg.slug }}
                className="flex items-center justify-between rounded-xl border border-border bg-card p-4 hover:border-primary/50 transition-colors"
              >
                <div className="min-w-0">
                  <div className="font-display font-semibold text-foreground truncate">{lg.name}</div>
                  <div className="text-xs text-muted-foreground">
                    /{lg.slug}
                    {badgeText && (
                      <span
                        className={
                          "ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide " +
                          (badgeWarn
                            ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
                            : "bg-muted text-muted-foreground")
                        }
                      >
                        {badgeText}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-primary text-sm shrink-0">View →</div>
              </Link>
            );
          })}
          <div className="pt-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/join-league">
                <Plus className="w-4 h-4" /> Join another league
              </Link>
            </Button>
          </div>
        </div>
      )}

      {requests.length > 0 && (
        <section className="mt-8">
          <h2 className="font-display text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" /> Pending requests
          </h2>
          <div className="space-y-2">
            {requests.map((req) => {
              const isPending = req.status === "pending";
              const isBusy = actingId === req.membershipId;
              const dateLabel = (() => {
                try {
                  return `${formatDistanceToNowStrict(new Date(req.requestedAt))} ago`;
                } catch {
                  return "recently";
                }
              })();
              return (
                <div
                  key={req.membershipId}
                  className="rounded-xl border border-border bg-card p-4"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={
                            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide border " +
                            (isPending
                              ? "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30"
                              : "bg-destructive/15 text-destructive border-destructive/30")
                          }
                        >
                          {isPending ? (
                            <>
                              <Clock className="w-3 h-3" /> Pending review
                            </>
                          ) : (
                            <>
                              <XCircle className="w-3 h-3" /> Not approved
                            </>
                          )}
                        </span>
                        <span className="font-display font-semibold text-foreground truncate">
                          {req.leagueName}
                        </span>
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        Requested {dateLabel}
                        {req.attemptCount > 1 && <> · attempt #{req.attemptCount}</>}
                      </div>
                      {!isPending && (
                        <div className="mt-2 text-sm text-muted-foreground">
                          {req.denialReason && req.denialReason.length > 0 ? (
                            <span className="italic">"{req.denialReason}"</span>
                          ) : (
                            <span>The admin didn't approve this request.</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {isPending ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={isBusy}
                          onClick={() => void handleCancel(req)}
                        >
                          <X className="w-3.5 h-3.5" /> Cancel request
                        </Button>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            disabled={isBusy}
                            onClick={() => void handleResubmit(req)}
                          >
                            <RefreshCcw className="w-3.5 h-3.5" /> Request again
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={isBusy}
                            onClick={() => void handleCancel(req)}
                            aria-label="Dismiss"
                          >
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="mt-8 flex gap-3 justify-center text-xs">
        <Link to="/profile" className="text-muted-foreground hover:text-foreground">
          Edit profile
        </Link>
      </div>
    </main>
    </>
  );
}
